/**
 * 盘中陪伴（聊天室）领域类型，与 lark-application 接口字段对齐。
 */

import type { MemberBadge } from "../../components/member-badge/MemberBadgeType";

export type CompanionMessageType = "TEXT" | "IMAGE" | "PDF" | string;

export type CompanionVisibility = "PUBLIC" | "PRIVATE" | string;

export type CompanionRejectedEventType = "SHIELD" | "RECALL" | string;

export type CompanionModerationStatus = "PENDING" | "APPROVED" | "REJECTED" | string;

/** 本人发送且审核未通过、仍可展示的正文（删除线 + 感叹号提示） */
export function companionIsSelfModerationRejected(m: CompanionMessageRow): boolean {
  if (!m.fromUser) return false;
  if (m.deleted || m.shielded) return false;
  return m.moderationStatus?.trim() === "REJECTED";
}

/** 是否当前登录用户发出的消息（群聊中 fromUser 含其他用户，需用 senderUserId 区分） */
export function companionMessageIsMine(m: CompanionMessageRow, userId: string): boolean {
  const u = userId.trim();
  if (!u) return false;
  return Boolean(m.fromUser) && (m.senderUserId?.trim() ?? "") === u;
}

export interface CompanionMessageRow {
  id: string;
  chatRoomId?: string;
  messageType: CompanionMessageType;
  content: string;
  fileUrl?: string | null;
  fileName?: string | null;
  fromUser?: boolean | null;
  senderUserId?: string | null;
  senderName?: string | null;
  /** 发送者当前头像 URL，可能为空 */
  senderAvatarUrl?: string | null;
  visibility?: CompanionVisibility | null;
  /** 后端返回：PENDING / APPROVED / REJECTED 等 */
  moderationStatus?: CompanionModerationStatus | null;
  replyToMessageId?: string | null;
  shielded?: boolean | null;
  deleted?: boolean | null;
  sentAt?: string | null;
  /** C 端仅 teacher | assistant：负责人与空间老师为 teacher，其余职员为 assistant，仅教职工消息可能有值 */
  senderSpaceRole?: string | null;
  /** 发送者在期会员标识（仅客户；无在期会员时为空） */
  senderMemberBadges?: MemberBadge[] | null;
  /** 仅前端：外发失败，可点感叹号重试 */
  outboundFailed?: boolean;
}

/** 前端重试外发时缓存的入参（不入库） */
export type CompanionOutboundRetryPayload =
  | {
      kind: "TEXT";
      content: string;
      replyToMessageId?: string;
    }
  | {
      kind: "IMAGE";
      fileUrl: string;
      fileName: string;
      replyToMessageId?: string;
    };

export interface RejectedEventRow {
  id: string;
  messageId: string;
  eventType: CompanionRejectedEventType;
}

export interface MyMessageThreadRow {
  myMessage: CompanionMessageRow;
  replies: CompanionMessageRow[];
  replyCount: number | null;
}

export interface CompanionRoomFileRow {
  messageId: string;
  fileUrl: string;
  fileName: string;
  messageType: CompanionMessageType;
  senderName: string;
  sentAt: string;
}

/** 各接口入参需带 spaceId（应用层强校验） */
export interface CompanionSpaceParam {
  spaceId: string;
}

export interface GetCompanionRoomInfoParameter extends CompanionSpaceParam {
  userId?: string;
  /** 群聊陪伴产品实例 amoeba_space_product.id */
  spaceProductId: string;
}

export interface GetCompanionRoomInfoResult {
  enabled: boolean;
  chatRoomId?: string | null;
  roomTitle?: string | null;
  chatOpen?: boolean | null;
  maxPerMinute?: number | null;
  maxPerDay?: number | null;
  remainPerDay?: number | null;
  maxImagePerDay?: number | null;
  remainImagePerDay?: number | null;
  maxDisplayCount?: number | null;
}

export interface EnterCompanionRoomParameter extends CompanionSpaceParam {
  userId?: string;
  chatRoomId: string;
}

export interface EnterCompanionRoomResult {
  chatRoomId: string;
  enteredAt: string;
}

export interface GetLatestCompanionMessagesParameter extends CompanionSpaceParam {
  userId?: string;
  chatRoomId: string;
  size?: number;
}

export interface GetLatestCompanionMessagesResult {
  list: CompanionMessageRow[];
  hasOlder?: boolean | null;
  maxApprovedId?: string | null;
  maxRejectedId?: string | null;
}

export interface GetOlderCompanionMessagesParameter extends CompanionSpaceParam {
  userId?: string;
  chatRoomId: string;
  anchorMessageId: string;
  size?: number;
}

export interface GetOlderCompanionMessagesResult {
  list: CompanionMessageRow[];
  hasMore?: boolean | null;
}

export interface PullNewCompanionMessagesParameter extends CompanionSpaceParam {
  userId?: string;
  chatRoomId: string;
  /** 客户端已见的最大消息 id（含本人待审公屏）；优先发送，服务端兼容旧字段 maxApprovedId */
  maxApprovedOrSelfMessageId?: string | null;
  maxApprovedId?: string | null;
  maxRejectedId?: string | null;
}

export interface PullNewCompanionMessagesResult {
  list: CompanionMessageRow[];
  hasMore?: boolean | null;
  maxApprovedId?: string | null;
  maxRejectedId?: string | null;
  rejectedEvents?: RejectedEventRow[] | null;
  maxPerDay?: number | null;
  remainPerDay?: number | null;
  maxImagePerDay?: number | null;
  remainImagePerDay?: number | null;
}

export interface SendCompanionMessageParameter extends CompanionSpaceParam {
  userId?: string;
  chatRoomId: string;
  messageType: CompanionMessageType;
  visibility: CompanionVisibility;
  content?: string;
  fileUrl?: string | null;
  fileName?: string | null;
  replyToMessageId?: string | null;
}

export interface SendCompanionMessageResult {
  message: CompanionMessageRow;
  maxPerMinute?: number | null;
  maxPerDay?: number | null;
  remainPerMinute?: number | null;
  remainPerDay?: number | null;
  maxImagePerDay?: number | null;
  remainImagePerDay?: number | null;
}

export interface GetMyMessagesWithRepliesParameter extends CompanionSpaceParam {
  userId?: string;
  chatRoomId: string;
  cursorSentAt?: string | null;
  cursorMessageId?: string | null;
  size?: number;
}

export interface GetMyMessagesWithRepliesResult {
  list: MyMessageThreadRow[];
  hasMore?: boolean | null;
  nextCursorSentAt?: string | null;
  nextCursorMessageId?: string | null;
}

export interface GetCompanionRoomFilesParameter extends CompanionSpaceParam {
  userId?: string;
  chatRoomId: string;
  cursorSentAt?: string | null;
  cursorMessageId?: string | null;
  size?: number;
}

export interface GetCompanionRoomFilesResult {
  list: CompanionRoomFileRow[];
  hasMore?: boolean | null;
  nextCursorSentAt?: string | null;
  nextCursorMessageId?: string | null;
}

export interface GetTopPinnedCompanionMessagesParameter extends CompanionSpaceParam {
  userId?: string;
  chatRoomId: string;
}

export interface GetTopPinnedCompanionMessagesResult {
  list: CompanionMessageRow[];
}

export type CompanionSidePanelKey = "mine" | "files" | "pinned";
